import type { ContaTablet, ItemConta } from '../api';

/**
 * Consolidação da conta da mesa/comanda.
 *
 * O Sischef devolve um registro por pedido — e nele entram tanto os pedidos do
 * cardápio quanto os que o garçom lançou no PDV, porque o PDV também grava
 * `PedidoIntegracao`. Somar todos é o que faz a conta do tablet bater com a do
 * caixa. Contas em centavos: somar float dá 0,01 de diferença, e diferença de
 * centavo na frente do cliente vira discussão.
 */
export interface ContaConsolidada {
  subtotal: number;
  desconto: number;
  taxaServico: number;
  total: number;
  quantidadeItens: number;
}

const centavos = (v: number | null | undefined): number => Math.round((v ?? 0) * 100);
const reais = (c: number): number => c / 100;

export function consolidarConta(conta: ContaTablet | undefined): ContaConsolidada {
  const pedidos = conta?.pedidos ?? [];

  let subtotal = 0;
  let desconto = 0;
  let taxaServico = 0;
  let total = 0;
  let quantidadeItens = 0;

  for (const p of pedidos) {
    const totalPedido = centavos(p.total);
    total += totalPedido;
    desconto += centavos(p.desconto);
    taxaServico += centavos(p.taxaServico);
    // Sem itens na resposta não há subtotal: o total do pedido é o que se sabe.
    subtotal += p.subtotal != null ? centavos(p.subtotal) : totalPedido;
    quantidadeItens += (p.itens ?? []).length;
  }

  return {
    subtotal: reais(subtotal),
    desconto: reais(desconto),
    taxaServico: reais(taxaServico),
    total: reais(total),
    quantidadeItens,
  };
}

/** Siglas de lançamento no balcão/PDV (PedidoTipoImportacao do Sischef). */
const ORIGENS_BALCAO = new Set(['SO', 'PO', 'PF', 'SC', 'SM']);

/**
 * Rótulo de origem do pedido, para o cliente entender por que a conta tem mais
 * do que ele pediu no aparelho. Integração de delivery não recebe rótulo: não
 * cai em conta de salão, e inventar nome de app na conta só confunde.
 */
export function rotuloOrigem(tipoImportacao: string | null | undefined): string | null {
  const sigla = (tipoImportacao ?? '').trim().toUpperCase();
  if (sigla === 'CS') return 'Pelo cardápio';
  if (ORIGENS_BALCAO.has(sigla)) return 'Com o atendente';
  return null;
}

/**
 * Itens da conta no formato da `ListaItensPedido` (mesma linha do carrinho).
 *
 * `imagens` é o mapa do catálogo indexado por id de produto: a conta vem do
 * Sischef com `codigoExterno`, que é o mesmo id em string. Sem ele a linha
 * mostra o quadrado de imagem quebrada, porque o espaço da foto é reservado
 * de qualquer jeito.
 */
export function itensParaListagem(
  itens: ItemConta[] | undefined,
  prefixoId: string,
  imagens?: Map<number, string | undefined>,
): Array<{
  id: string;
  nome: string;
  quantidade: number;
  valorTotal: number;
  imagemUrl?: string;
  subitens: Array<{ nome: string; quantidade: number; valorTotal: number }>;
}> {
  return (itens ?? []).map((item, i) => ({
    id: `${prefixoId}-${item.codigoExterno ?? i}`,
    nome: item.nome ?? 'Item',
    quantidade: item.quantidade ?? 1,
    valorTotal: item.valorTotal ?? 0,
    imagemUrl: imagens?.get(Number(item.codigoExterno)),
    subitens: (item.subItens ?? []).map((sub) => ({
      nome: sub.nome ?? '',
      quantidade: sub.quantidade ?? 1,
      valorTotal: sub.valorTotal ?? 0,
    })),
  }));
}
